/** Keep layout in application code; the browser only receives prose fragments. */
export async function translatePromptText(
  source: string,
  translate: (text: string) => Promise<string>,
  isCancelled: () => boolean = () => false,
  onProgress: (line: number, total: number) => void = () => {},
): Promise<string> {
  const pieces = source.split(/(\r\n|\n|\r)/);
  const total = Math.ceil(pieces.length / 2);
  let fence: { character: string; length: number } | undefined;
  const checkCancelled = () => {
    if (isCancelled()) throw new Error("Translation cancelled");
  };
  const prose = async (value: string) => {
    checkCancelled();
    // Preserve leading/trailing whitespace, including Markdown hard line breaks.
    const match = /^(\s*)(.*?)(\s*)$/.exec(value)!;
    if (!/[a-zA-Z]/.test(match[2])) return value;
    const translated = await translate(match[2]);
    checkCancelled();
    if (!translated.trim()) throw new Error("Empty translation");
    // A translation cannot add layout that wasn't in the source fragment.
    return match[1] + translated.trim().replace(/[\r\n]+/g, " ") + match[3];
  };
  const inline = async (value: string) => {
    // Keep inline code, escapes, links, URLs, emphasis and table boundaries intact.
    const markers =
      /(`+)[\s\S]*?\1|\\[\\`*{}\[\]()#+\-.!_>|]|!?\[|\]\([^\r\n]*?\)|https?:\/\/[^\s<>]+|\*{1,3}|_{1,3}|~~|\|/g;
    let result = "",
      offset = 0;
    for (const token of value.matchAll(markers)) {
      result += await prose(value.slice(offset, token.index));
      result += token[0];
      offset = token.index! + token[0].length;
    }
    return result + (await prose(value.slice(offset)));
  };
  for (let index = 0; index < pieces.length; index += 2) {
    checkCancelled();
    const line = pieces[index];
    onProgress(index / 2 + 1, total);
    // Peel off quote/list/heading markers and indentation without translating them.
    const prefix =
      /^(?:[\t ]+|>[\t ]?|#{1,6}[\t ]+|(?:[-+*]|\d+[.)])[\t ]+|\[[ xX]\][\t ]+)*/.exec(
        line,
      )![0];
    const body = line.slice(prefix.length);
    const marker = /^(`{3,}|~{3,})(.*)$/.exec(body);
    if (fence) {
      if (
        marker &&
        marker[1][0] === fence.character &&
        marker[1].length >= fence.length &&
        !marker[2].trim()
      )
        fence = undefined;
      continue;
    }
    if (marker) {
      fence = { character: marker[1][0], length: marker[1].length };
      continue;
    }
    // Blank lines, rules, setext headings and table separators are structural.
    if (
      !line.trim() ||
      /^[\t ]*(?:[-*_][\t ]*){3,}$/.test(line) ||
      /^[\t ]*[=-]+[\t ]*$/.test(line) ||
      /^[\t |:-]+$/.test(line)
    )
      continue;
    // Keep optional closing hashes in ATX headings.
    const closing = /#{1,6}[\t ]+/.test(prefix)
      ? body.match(/[\t ]+#+[\t ]*$/)?.[0] || ""
      : "";
    pieces[index] =
      prefix +
      (await inline(closing ? body.slice(0, -closing.length) : body)) +
      closing;
  }
  checkCancelled();
  return pieces.join("");
}
